import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useContracts, useContractTransition, openContractPdf } from "../../api/contracts";
import { useDebounce } from "../../hooks/useDebounce";
import { StatusTag } from "../../components/StatusTag";
import { PaginationBar } from "../../components/PaginationBar";
import { formatBRL, formatDate } from "../../utils/money";
import { NewContractModal } from "./NewContractModal";
import { ReadjustContractModal } from "./ReadjustContractModal";
import { EditContractModal } from "./EditContractModal";
import { useToast } from "../../components/ToastProvider";
import { EditIconButton, PdfIconButton } from "../../components/RowIconButton";
import { Contract } from "../../types/dtos";

const READJUSTMENT_LABEL: Record<string, string> = { IPCA: "IPCA", IGPM: "IGP-M", NONE: "—" };

export default function ContractsPage() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [readjusting, setReadjusting] = useState<Contract | null>(null);
  const [editing, setEditing] = useState<Contract | null>(null);
  const debouncedQ = useDebounce(q);
  const { data, isLoading, isError } = useContracts({ page, pageSize: 7, q: debouncedQ || undefined });
  const transition = useContractTransition();
  const toast = useToast();

  async function handleTransition(id: number, action: "suspend" | "reactivate" | "cancel") {
    await transition.mutateAsync({ id, action });
    toast(
      action === "suspend" ? "Contrato suspenso." : action === "reactivate" ? "Contrato reativado." : "Contrato encerrado."
    );
  }

  async function handlePdf(id: number) {
    try {
      await openContractPdf(id);
    } catch {
      toast("Não foi possível gerar o PDF do contrato.");
    }
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="row g-2">
        <div className="col-md-6">
          <label className="nt-field-label">Buscar</label>
          <input
            className="form-control"
            placeholder="Buscar por cliente, aplicação ou contrato…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-md-3" />
        <div className="col-md-3">
          <label className="nt-field-label d-none d-md-block">&nbsp;</label>
          <button type="button" className="btn btn-primary w-100" onClick={() => setShowNew(true)}>
            Novo contrato
          </button>
        </div>
      </div>

      {isLoading && <div className="text-secondary">Carregando…</div>}
      {isError && <div className="text-danger">Não foi possível carregar os contratos.</div>}

      {data && (
        <div className="nt-table-wrap" style={{ overflowX: "auto" }}>
          <table className="table nt-table-fade table-striped align-middle">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Contrato</th>
                <th style={{ width: 280 }}>Cliente</th>
                <th style={{ width: 190 }}>Aplicação</th>
                <th style={{ width: 120 }}>Dia de cobrança</th>
                <th style={{ width: 110 }}>Valor</th>
                <th style={{ width: 140 }}>Próxima cobrança</th>
                <th style={{ width: 100 }}>Reajuste</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 210 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((c) => (
                <tr key={c.id}>
                  <td className="nt-tabular">{c.code}</td>
                  <td style={{ fontFamily: "var(--font-heading)" }}>{c.customer.razaoSocial}</td>
                  <td>{c.plan.application.name}</td>
                  <td>dia {c.billingDay}</td>
                  <td className="text-end nt-tabular">{formatBRL(c.monthlyValue)}</td>
                  <td className="nt-tabular">{formatDate(c.nextChargeDate)}</td>
                  <td>{READJUSTMENT_LABEL[c.readjustmentIndex]}</td>
                  <td>
                    <StatusTag status={c.status} />
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end align-items-center">
                      <EditIconButton onClick={() => setEditing(c)} />
                      <PdfIconButton onClick={() => handlePdf(c.id)} />
                      {c.status !== "ENCERRADO" && (
                        <Dropdown align="end">
                          <Dropdown.Toggle variant="secondary" size="sm">
                            Ações
                          </Dropdown.Toggle>
                          {/* strategy fixed: o menu escapa do overflow da caixa da tabela */}
                          <Dropdown.Menu popperConfig={{ strategy: "fixed" }} renderOnMount>
                            <Dropdown.Item as="button" onClick={() => setReadjusting(c)}>
                              Reajustar
                            </Dropdown.Item>
                            {c.status === "VIGENTE" && (
                              <Dropdown.Item as="button" onClick={() => handleTransition(c.id, "suspend")}>
                                Suspender
                              </Dropdown.Item>
                            )}
                            {c.status === "SUSPENSO" && (
                              <Dropdown.Item as="button" onClick={() => handleTransition(c.id, "reactivate")}>
                                Reativar
                              </Dropdown.Item>
                            )}
                            <Dropdown.Divider />
                            <Dropdown.Item as="button" className="text-danger" onClick={() => handleTransition(c.id, "cancel")}>
                              Encerrar
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-secondary text-center py-4">
                    Nenhum contrato encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && <PaginationBar page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />}

      <NewContractModal show={showNew} onClose={() => setShowNew(false)} />
      <ReadjustContractModal contract={readjusting} onClose={() => setReadjusting(null)} />
      <EditContractModal contract={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
